import { Flex } from "@chakra-ui/react";
import { QuestionCard } from "newui/features/Rfx/Creation/components/ReturnableSchedule/QuestionCard";
import { FieldForm } from "components/RFQ/Input/Input";
import React, { useRef } from "react";
import { QuestionComponent } from "./QuestionTypeComponent";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { reorderList } from "utils/reorderList";
import { useField } from "formik";
import {
	scrollAndFocusOnQuestion,
	sortRemovableAndSortOrderItems,
} from "newui/features/Rfx/Creation/components/ReturnableSchedule/utils";
import { showToast } from "utils/helpers";

const SectionContent = ({ OptionSections, index }) => {
	const fieldname = (name) => `sections[${index}]${name}`;
	const [, meta] = useField(fieldname("questionnaire"));

	return (
		<div className="mb-5 space-y-3">
			<Flex gap={5}>
				<FieldForm
					placeholder="Add Section Name: E.g. Returnable Schedule 1.0"
					name={fieldname("name")}
				/>
			</Flex>
			<Flex>
				<FieldForm
					type="textArea"
					name={fieldname("description")}
					placeholder="Add Section Description: E.g. Program and Methodology"
				/>
			</Flex>
			{meta?.error && typeof meta.error === "string" && meta.touched ? (
				<p className="text-md mt-2 text-red-500">{meta.error}</p>
			) : null}
		</div>
	);
};

const ReturnableSchedulesCard = ({
	OptionSections,
	isSmartField,
	dragHandle,
	index,
	deleteSectionsHandler,
	copySectionsHandler,
	sectionList,
	setFieldError,
	section,
}) => {
	const fieldname = (name) => `sections[${index}]${name}`;
	const [, meta, helper] = useField(fieldname("questionnaire"));
	const questionsValue = meta.value;
	const sectionIndex = index;
	const dragEndHandler = (result) => {
		let value = questionsValue;
		let valueSetter = (newValue) => (value = newValue);
		reorderList(value, result, valueSetter);
		const reordered = value.map((item, index) => ({
			...item,
			sort_order: index + 1,
		}));
		helper.setValue(reordered);
	};

	// we need this to prevent duplication
	const lastCopiedQuestion = useRef(null);
	const lastDeletedQuestion = useRef(null);

	const copyQuestionHandler = (index) => {
		if (lastCopiedQuestion.current === index) {
			return;
		}

		lastCopiedQuestion.current = index;

		const questionBeingCopied = meta.value[index];

		const copiedQuestion = {
			...questionBeingCopied,
			sort_order: meta.value.length + 1,
			id: crypto.randomUUID(),
			uuid: Math.floor(1000 + Math.random() * 9000),
			name: `(Copy) ${questionBeingCopied.name}`,
			isRemove: 0,
		};

		helper.setValue(
			sortRemovableAndSortOrderItems([...meta.value, copiedQuestion])
		);

		scrollAndFocusOnQuestion(copiedQuestion.id);

		// reset after a short delay to allow next copy
		setTimeout(() => (lastCopiedQuestion.current = null), 50);

		showToast(null, "Question Copied", true);
	};

	const deleteQuestionHandler = (index) => {
		if (lastDeletedQuestion.current === index) {
			return;
		}

		// start copying this section
		lastDeletedQuestion.current = index;

		const deletedQuestion = meta.value[index];

		const existingQuestions = meta.value;

		// if we haven't saved the section being deleted yet, we can just remove it from the results
		const newQuestions = [];

		// cycle through existing questions
		for (
			let questionIndex = 0;
			questionIndex < existingQuestions.length;
			questionIndex++
		) {
			if (questionIndex === index && isNaN(deletedQuestion.id)) {
				// we can just remove it
				continue;
			}

			// if deleted item, reset the data
			const question =
				questionIndex === index
					? { ...deletedQuestion, isRemove: 1, fields: [] }
					: { ...existingQuestions[questionIndex] };

			// we will update any sort_order as needed before we push
			if (questionIndex > index) {
				question.sort_order -= 1;
			}

			newQuestions.push(question);
		}

		helper.setValue(sortRemovableAndSortOrderItems(newQuestions));
		typeof meta.error !== "string" && helper.setError(undefined);

		// reset after a short delay to allow next deletion
		setTimeout(() => (lastDeletedQuestion.current = null), 50);

		showToast(
			"You must save progress to delete permanently",
			`Question Deleted`,
			true
		);
	};

	const isSectionDeletable = () => {
		let count = 0;

		// get a count of sections not removed
		for (let i = 0; i < sectionList?.length; i++) {
			if (sectionList[i].isRemove === 0) {
				count++;
			}
		}

		return count > 1;
	};

	const isQuestionnaireDeletable = (index) => {
		let count = 0;

		// get a count of questions not removed
		for (let i = 0; i < sectionList?.[index].questionnaire?.length; i++) {
			if (sectionList?.[index].questionnaire[i].isRemove === 0) {
				count++;
			}

			// no need to continue once this is true
			if (count > 1) {
				break;
			}
		}

		return count > 1;
	};

	return (
		<div
			className="h-max w-full scroll-pt-8"
			id={`section-container-${section.id}`}
		>
			{
				<QuestionCard
					dragHandle={dragHandle}
					isSection={true}
					sectionIndex={index}
					isSmartField={isSmartField}
					deleteSectionHandler={() => deleteSectionsHandler(index)}
					copySectionHandler={() => copySectionsHandler(index)}
					id={`section-${index}`}
					isDeletable={isSectionDeletable()}
				>
					<SectionContent
						OptionSections={OptionSections}
						index={index}
					/>
				</QuestionCard>
			}
			<DragDropContext
				onDragEnd={dragEndHandler}
				className="my-10"
			>
				{/* index = section index and i = questionnaire index */}
				<Droppable
					droppableId={`questioner-container-${index}`}
					direction="vertical"
				>
					{(provided) => (
						<div
							id="questioner-container"
							className="mb-8"
							{...provided.droppableProps}
							ref={provided.innerRef}
						>
							{questionsValue &&
								questionsValue.length > 0 &&
								questionsValue?.map((question, i) => {
									return question?.isRemove !== 1 ? (
										<span id={`question-display-${question.id}`}>
											<Draggable
												key={`question-${question?.id}`}
												draggableId={`question-${question?.id}`}
												index={i}
											>
												{(provided) => (
													<div
														ref={provided.innerRef}
														{...provided.draggableProps}
													>
														<QuestionCard
															dragHandle={provided.dragHandleProps}
															sectionIndex={index}
															index={i}
															copyQuestionHandler={() => copyQuestionHandler(i)}
															deleteQuestionHandler={() =>
																deleteQuestionHandler(i)
															}
															id={`question-${index}-${i}`}
															isDeletable={isQuestionnaireDeletable(index)}
														>
															<QuestionComponent
																index={i}
																sectionIndex={index}
																question={question}
																isDeletable
																setFieldError={setFieldError}
															/>
														</QuestionCard>
													</div>
												)}
											</Draggable>
										</span>
									) : null;
								})}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		</div>
	);
};

export default ReturnableSchedulesCard;
